import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import { toast } from 'react-toastify';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import ModalExpediente from '../../nuevosPostulantes/ModalExpediente';
import {
    getPruebaConocimientoAPI,
    resultadoPruebaConocimientoAPI,
    getExpedientePostulacionAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// HU-PR-05 CA-10 a CA-15: "[Campaña] – Resultado Prueba de conocimientos".
//
// CA-11 pide mostrar "la ficha de resultados que actualmente existe en el Módulo
// de Pruebas", así que la ficha NO se reescribe: se reutiliza ModalExpediente,
// que ya sabe normalizar ese payload y calcular la nota vigesimal. Rehacerla
// sería contradecir la HU y además duplicar una lógica que cambia del otro lado.
//
// Los tres botones no envían ningún correo (R-12): registrar el resultado y
// comunicarlo son cosas distintas, y la comunicación la dispara GDH después desde
// el ícono de avión de la Bandeja de Postulantes.
// ──────────────────────────────────────────────────────────────────────────

// dbo.Postulacion.IdEstado — el estado del lado del Módulo de Evaluaciones.
const ESTADO_POSTULACION = {
    NUEVO: 47,
    ENVIADO: 18,
    PROCESO: 16,
    COMPLETADA: 9,
    VENCIDO: 11,
    CANCELADO: 25,
};

const ResultadoPruebaConocimiento = ({ toggleModal, refreshPage, postulante = {}, onVolverADerivar }) => {
    const idPostulante = postulante?.idPostulante;

    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [prueba, setPrueba] = useState(null);
    const [expediente, setExpediente] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [mostrarObservacion, setMostrarObservacion] = useState(false);
    const [observacion, setObservacion] = useState('');

    // ModalExpediente NO es un bloque de contenido: se pinta con createPortal sobre
    // document.body, con su propio overlay a pantalla completa. Renderizarlo acá
    // adentro dejaba dos modales superpuestos, y como su onClose era un noop el
    // único botón que respondía era el de este modal — que al desmontarse se
    // llevaba el portal. Por eso ahora es un segundo paso, con su cierre real.
    const [verExpediente, setVerExpediente] = useState(false);

    useEffect(() => {
        if (!idPostulante) return;
        let cancelado = false;

        (async () => {
            const pruebaResp = await executeGet(getPruebaConocimientoAPI(idPostulante));
            if (cancelado) return;

            const datosPrueba = pruebaResp?.data ?? null;
            setPrueba(datosPrueba);

            // El expediente solo existe del lado del Módulo de Evaluaciones y solo
            // tiene sentido pedirlo cuando el candidato empezó a rendir.
            const idPostulacion = datosPrueba?.IdPostulacion;
            const yaEmpezo = datosPrueba?.IdEstadoPostulacion === ESTADO_POSTULACION.PROCESO
                || datosPrueba?.IdEstadoPostulacion === ESTADO_POSTULACION.COMPLETADA;

            if (idPostulacion && yaEmpezo) {
                const expedienteResp = await executeGet(getExpedientePostulacionAPI(idPostulacion));
                if (!cancelado) setExpediente(expedienteResp?.data ?? null);
            }

            if (!cancelado) setIsLoading(false);
        })();

        return () => { cancelado = true; };
    }, [idPostulante]);

    const registrarDecision = async (decision, textoObservacion) => {
        setIsSending(true);
        try {
            await managePost(
                resultadoPruebaConocimientoAPI(),
                {
                    IdPostulante: idPostulante,
                    Decision: decision,
                    Observacion: textoObservacion ?? null,
                },
                (response) => {
                    // "Volver a rendir" (R): el .NET ya intenta re-derivar la misma
                    // evaluación solo (ResultadoPruebaConocimiento -> DerivarAlModuloDeEvaluaciones),
                    // sin pedirle nada a GDH. RequiereConfiguracionManual indica si esa
                    // auto-derivación funcionó o no — abrir "Configurar prueba" siempre,
                    // sin mirar este flag, terminaba creando una SEGUNDA postulación
                    // sobre una que la auto-derivación ya había dejado activa, y el
                    // Módulo de Evaluaciones la rechazaba ("ya existe una postulación
                    // activa"). Solo hace falta el puente manual cuando de verdad no se
                    // pudo derivar sola (p.ej. no se pudieron recuperar las evaluaciones
                    // del intento anterior) — mismo puente que ya usa el botón "Volver a
                    // derivar la prueba" de más abajo (caso vencido/cancelado).
                    const requiereConfiguracionManual = decision === 'R'
                        && response?.data?.RequiereConfiguracionManual === true;

                    toast.success(
                        decision === 'A' ? 'Postulante marcado como Apto'
                            : decision === 'N' ? 'Postulante marcado como No apto'
                                : requiereConfiguracionManual
                                    ? 'Intento anterior cerrado. Complete los datos para derivar el nuevo intento.'
                                    : 'Intento anterior cerrado. Se derivó automáticamente un nuevo intento con la misma evaluación.');
                    refreshPage && refreshPage();
                    toggleModal();
                    if (requiereConfiguracionManual) {
                        onVolverADerivar && onVolverADerivar(postulante);
                    }
                },
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleVolverARendir = () => {
        if (!mostrarObservacion) {
            // CA-13 / R-14: el motivo es obligatorio, así que se pide antes de
            // mandar nada. El backend lo valida igual.
            setMostrarObservacion(true);
            return;
        }
        if (!observacion.trim()) {
            toast.warn('Indique el motivo por el que debe volver a rendir');
            return;
        }
        registrarDecision('R', observacion.trim());
    };

    const idEstadoPostulacion = prueba?.IdEstadoPostulacion;
    const yaCalifico = prueba?.IdEstadoPruebaConocimiento != null;
    const puedeCalificar = idEstadoPostulacion === ESTADO_POSTULACION.COMPLETADA && !yaCalifico;
    const pruebaTruncada = idEstadoPostulacion === ESTADO_POSTULACION.VENCIDO
        || idEstadoPostulacion === ESTADO_POSTULACION.CANCELADO;

    // Mientras se mira la ficha, esta pantalla no se renderiza: se reemplazan en vez
    // de apilarse. ModalExpediente sale por portal a document.body con su propio
    // overlay, así que teniendo los dos montados quedaban dos capas superpuestas.
    // El estado no se pierde: el componente sigue vivo, solo cambia lo que devuelve,
    // y al cerrar la ficha se vuelve acá con los botones de decisión intactos.
    if (verExpediente) {
        return (
            <ModalExpediente
                idPostulacion={prueba?.IdPostulacion}
                expedientePayload={expediente}
                isOpen
                onClose={() => setVerExpediente(false)}
            />
        );
    }

    const tituloCampana = postulante?.nombreConvocatoria
        ? `${postulante.nombreConvocatoria} – Resultado Prueba de conocimientos`
        : 'Resultado Prueba de conocimientos';

    return (
        <GenericModal showModal={toggleModal} titulo={tituloCampana} width="900px">
            <div className="modal-body pt-3">

                {isLoading
                    ? <div className="text-center py-5"><FontAwesomeIcon icon="spinner" className="spinner" /> Cargando…</div>
                    : !prueba
                        ? <AvisoBox>Este candidato todavía no fue derivado a Prueba de Conocimientos.</AvisoBox>
                        : (
                            <>
                                <div className="displayFlexWrap">
                                    <div className="col-md-4"><InputText label="Intento" name="intento" value={`N° ${prueba.NroIntento ?? 1}`} direction="row" isDisabled /></div>
                                    <div className="col-md-4"><InputText label="Estado de la prueba" name="estadoPrueba" value={prueba.DesEstadoPostulacion ?? '—'} direction="row" isDisabled /></div>
                                    <div className="col-md-4"><InputText label="Fecha límite" name="fechaLimite" value={prueba.FechaLimiteEvaluacion ?? '—'} direction="row" isDisabled /></div>
                                    {yaCalifico && (
                                        <>
                                            <div className="col-md-4"><InputText label="Resultado" name="resultado" value={prueba.DesEstadoPruebaConocimiento ?? '—'} direction="row" isDisabled /></div>
                                            <div className="col-md-4"><InputText label="Fecha del resultado" name="fechaResultado" value={prueba.FechaResultado ?? '—'} direction="row" isDisabled /></div>
                                        </>
                                    )}
                                </div>

                                {/* Si Selección dio de baja la postulación o se venció el
                                    plazo, el resultado no va a llegar nunca. Se dice
                                    explícitamente en vez de dejar la pantalla en blanco. */}
                                {pruebaTruncada && (
                                    <AvisoBox className="mt-3">
                                        {idEstadoPostulacion === ESTADO_POSTULACION.CANCELADO
                                            ? 'La postulación fue dada de baja en el Módulo de Evaluaciones, así que el candidato no va a rendir esta prueba.'
                                            : 'Venció el plazo para rendir la prueba y el candidato no la completó.'}
                                        {' '}Puede volver a derivarlo con el botón de abajo.
                                    </AvisoBox>
                                )}

                                <hr />

                                {/* CA-11: la ficha que ya existe en el Módulo de Pruebas. Se abre
                                    aparte porque ModalExpediente es un modal completo, no un
                                    bloque: se monta con createPortal sobre document.body. */}
                                {expediente
                                    ? (
                                        <div className="text-center">
                                            <button type="button" className="btn generic-button-buscador"
                                                onClick={() => setVerExpediente(true)}>
                                                <FontAwesomeIcon icon="file-invoice" className="mr-2" />
                                                Ver ficha de resultados
                                            </button>
                                        </div>
                                    )
                                    : !pruebaTruncada && (
                                        <AvisoBox>
                                            El candidato todavía no completó la evaluación. Los resultados van a
                                            estar disponibles acá cuando la termine.
                                        </AvisoBox>
                                    )
                                }

                                {mostrarObservacion && (
                                    <div className="mt-3">
                                        <label className="modal-label">Motivo para volver a rendir</label>
                                        <textarea className="form-control" rows="3" value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            placeholder="Indique por qué el candidato debe repetir la evaluación" />
                                    </div>
                                )}

                                {/* CA-12: las tres acciones, solo cuando hay algo que decidir */}
                                {puedeCalificar && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <button type="button" disabled={isSending} className="btn btn-success mr-3"
                                            onClick={() => registrarDecision('A')}>
                                            Apto {isSending && (<FontAwesomeIcon icon='spinner' transform="left-9" className="spinner ml-3" />)}
                                        </button>
                                        <button type="button" disabled={isSending} className="btn btn-danger mr-3"
                                            onClick={() => registrarDecision('N')}>
                                            No Apto
                                        </button>
                                        <button type="button" disabled={isSending} className="btn btn-warning"
                                            onClick={handleVolverARendir}>
                                            {mostrarObservacion ? 'Confirmar volver a rendir' : 'Observar / Volver a rendir'}
                                        </button>
                                    </div>
                                )}

                                {/* Salida para la prueba que murió del otro lado: sin esto
                                    el candidato queda trabado — no puede rendir ni se le
                                    puede derivar otra desde la bandeja, porque el ícono
                                    "Configurar prueba" solo aparece en la etapa anterior. */}
                                {pruebaTruncada && !yaCalifico && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <button type="button" disabled={isSending} className="btn generic-button-buscador"
                                            onClick={() => { toggleModal(); onVolverADerivar && onVolverADerivar(postulante); }}>
                                            Volver a derivar la prueba
                                        </button>
                                    </div>
                                )}

                                {yaCalifico && (
                                    <div className="text-center mt-4 text-muted">
                                        Esta prueba ya fue calificada y no admite una nueva decisión.
                                    </div>
                                )}
                            </>
                        )
                }
            </div>

        </GenericModal>
    )
}

export default ResultadoPruebaConocimiento

const AvisoBox = styled.div`
    border: 1px solid #ffe08a;
    background: #fff9e6;
    border-radius: 8px;
    padding: 16px;
    color: #8a6d3b;
`;
